import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormioModule } from '@formio/angular';
import { FormioGrid } from '@formio/angular/grid';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { environment } from '../../environments/environment';
import { Formio } from '@formio/angular';
import { Subscription } from 'rxjs';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { LoadingService } from '../service/loading.service';
import { ChangeDetectorRef } from '@angular/core';
import { AppInsightsService } from '../service/app-insights.service';
import { FileUploadService } from '../service/file-upload.service';
import { PI_CONSENT_PDF, unbrandedConsentPdfUrl, WC_FORMS_PDF } from '../service/unbranded-forms.util';
import { AutosaveState, FormAutosaveService } from '../service/form-autosave.service';

import premium from '@formio/premium';

Formio.license = environment.LICENSE;
Formio.setBaseUrl(environment.BASE_URL);
Formio.setProjectUrl(environment.PROJECT_URL);
Formio.use(premium)

// "Is the patient covered by Insurance?" values that do not get a Curve record
const CURVE_EXCLUDED_INSURANCES = [
  'None/Selfpay',
  'Automobile (Motor Vehicle Accident ONLY)',
  'Workers Comp (Injury During Work ONLY)',
  'Veterans Administration(VA)',
];


@Component({
  selector: 'app-intake-form',
  standalone: true,
  imports: [CommonModule, RouterModule, FormioGrid, FormioModule, HttpClientModule],
  templateUrl: './new-intake.component.html',
  styleUrls: ['./new-intake.component.css'],
})
export class NewIntakeComponent implements OnInit, OnDestroy {
  submissionUrl: string = '';
  formJson: any;
  form: any;
  baseUrl: string = environment.BASE_URL;
  projectUrl: string = environment.PROJECT_URL;
  submissionData: any = {};
  currentRecordSubscription?: Subscription;
  submissionId: string | null = null;
  formData: any;
  token: string | null = null;
  linkExpired: boolean = false;
  // Error when required subscriber fields are missing on submit.
  submitError: string | null = null;
  private routeSub?: Subscription;
  private actualRoleValue: string = 'CT';
  currentCardName: string | null = null;
  private clearButtonObserver?: MutationObserver;
  private enterCheckboxHandler?: (e: KeyboardEvent) => void;

  // Serialized field value at focus time, compared on blur
  private fieldValuesOnFocus = new Map<string, string>();
  // Component currently in focus
  private focusedComponent: any = null;
  // Autosave state, armed after the form finishes hydrating
  autosaveState: AutosaveState = { status: 'idle', lastSavedAt: null, message: null };
  private autosaveArmed: boolean = false;
  private autosaveSub?: Subscription;
  private unloadFlushHandler?: () => void;

  // Insurance image URLs at the moment the form was loaded. Used so that the
  // queue request only fires when something was actually added/changed in this
  // session — matches the Clinic App behaviour and the API's dedup contract.
  private initialInsuranceImages: {
    primaryFront: string;
    primaryBack: string;
    secondaryFront: string;
    driversLicense: string;
    secondaryBack: string;
    autoInsurance: string;
  } = { primaryFront: '', primaryBack: '', secondaryFront: '', secondaryBack: '', driversLicense: '', autoInsurance: '' };
  private initialPatientInfo: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    insuranceName: string;
    clinicId: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    relationshipToPatient: string;
    subscriberFirstName: string;
    subscriberLastName: string;
    daySubscriberDOB: string;
    secondarySubscriberFirstName: string;
    secondarySubscriberLastName: string;
    daySecondaryDOB: string;
    secondaryRelationshipToPatientNew: string;
    policyHolder: string;
  } = { firstName: '', lastName: '', dateOfBirth: '', insuranceName: '', clinicId: '', address: '', city: '', state: '', zip: '', relationshipToPatient: '', subscriberFirstName: '', subscriberLastName: '', daySubscriberDOB: '', secondarySubscriberFirstName: '', secondarySubscriberLastName: '', daySecondaryDOB: '', secondaryRelationshipToPatientNew: '', policyHolder: '' };

  constructor(
    private router: Router,
    private loadingService: LoadingService,
    private route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private ai: AppInsightsService,
    private fileUploadService: FileUploadService,
    private http: HttpClient,
    private autosave: FormAutosaveService
  ) {}

  private extractImageUrl(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && value[0]?.url) return value[0].url;
    if (typeof value === 'object' && value.url) return value.url;
    return '';
  }

  private stripQuery(url: string): string {
    return url ? url.split('?')[0] : '';
  }

  private getInsuranceImagesFromData(data: any): {
    primaryFront: string;
    primaryBack: string;
    secondaryFront: string;
    secondaryBack: string;
    driversLicense: string;
    autoInsurance: string;
  } {
    return {
      primaryFront: this.extractImageUrl(data?.container6?.fileUploadFront),
      primaryBack: this.extractImageUrl(data?.container6?.fileUploadBack),
      secondaryFront: this.extractImageUrl(data?.container4?.secondarylicenseUploadFront),
      secondaryBack: this.extractImageUrl(data?.container4?.secondarylicenseUploadBack),
      driversLicense: this.extractImageUrl(data?.licenseUploadFront),
      autoInsurance: this.extractImageUrl(data?.uploadYourAutoInsurance),
    };
  }

  private getPatientInfoFromData(data: any): {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    insuranceName: string;
    clinicId: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    relationshipToPatient: string;
    subscriberFirstName: string;
    subscriberLastName: string;
    daySubscriberDOB: string;
    secondarySubscriberFirstName: string;
    secondarySubscriberLastName: string;
    daySecondaryDOB: string;
    secondaryRelationshipToPatientNew: string;
    policyHolder: string;
  } {
    return {
      firstName: data?.firstName || '',
      lastName: data?.lastName || '',
      dateOfBirth: data?.day || '',
      insuranceName: data?.isPatinetCoveredByInsurance || '',
      clinicId: data?.selectChiroCenter || '',
      address: data?.addresspatientInfo || '',
      city: data?.cityPatientInfo || '',
      state: data?.statepatientInfo || '',
      zip: data?.zipPatientInfo || '',
      relationshipToPatient: data?.container6?.relationshipToPatientNew || '',
      subscriberFirstName: data?.container6?.subscriberFirstName || '',
      subscriberLastName: data?.container6?.subscriberLastName || '',
      daySubscriberDOB: data?.container6?.daySubscriberDOB || '',
      secondarySubscriberFirstName: data?.container4?.secondarySubscriberFirstName || '',
      secondarySubscriberLastName: data?.container4?.secondarySubscriberLastName || '',
      daySecondaryDOB: data?.container4?.daySecondaryDOB || '',
      secondaryRelationshipToPatientNew: data?.container4?.secondaryRelationshipToPatientNew || '',
      policyHolder: data?.container6?.ifInsuredWhoIsTheMainSubscriberOrPolicyHolder || '',
    };
  }

  private captureInitialInsuranceImages(data: any): void {
    this.initialInsuranceImages = this.getInsuranceImagesFromData(data);
    this.initialPatientInfo = this.getPatientInfoFromData(data);
  }

  // Live form first so a mid session clinic change counts
  private get clinicCode(): string {
    return (
      this.form?.submission?.data?.selectChiroCenter ||
      this.formData?.data?.selectChiroCenter ||
      ''
    );
  }

  // Alaska gets the logo-free versions, see unbranded-forms.util
  consentPdfUrl(fileName: string): string {
    return unbrandedConsentPdfUrl(this.clinicCode, fileName);
  }

  private sendImagesToQueue(submissionId: string, data: any): void {

    // Only send to the queue for clinics explicitly allow-listed via environment.
    const clinicCode = data.selectChiroCenter || '';
    const allowedClinics = environment.QUEUE_ALLOWED_CLINICS || [];
    const allAllowed = allowedClinics.includes('*');

    if (!allAllowed && !allowedClinics.includes(clinicCode)) return;

    const current = this.getInsuranceImagesFromData(data);
    const initial = this.initialInsuranceImages;
    const currentPatient = this.getPatientInfoFromData(data);
    const initialPatient = this.initialPatientInfo;
    const imagesChanged =
      this.stripQuery(current.primaryFront) !== this.stripQuery(initial.primaryFront) ||
      this.stripQuery(current.primaryBack) !== this.stripQuery(initial.primaryBack) ||
      this.stripQuery(current.secondaryFront) !== this.stripQuery(initial.secondaryFront) ||
      this.stripQuery(current.driversLicense) !== this.stripQuery(initial.driversLicense) ||
      this.stripQuery(current.secondaryBack) !== this.stripQuery(initial.secondaryBack) ||
      this.stripQuery(current.autoInsurance) !== this.stripQuery(initial.autoInsurance);

    const patientChanged =
      currentPatient.firstName !== initialPatient.firstName ||
      currentPatient.lastName !== initialPatient.lastName ||
      currentPatient.dateOfBirth !== initialPatient.dateOfBirth ||
      currentPatient.insuranceName !== initialPatient.insuranceName ||
      currentPatient.address !== initialPatient.address ||
      currentPatient.city !== initialPatient.city ||
      currentPatient.state !== initialPatient.state ||
      currentPatient.zip !== initialPatient.zip ||
      currentPatient.relationshipToPatient !== initialPatient.relationshipToPatient ||
      currentPatient.subscriberFirstName !== initialPatient.subscriberFirstName ||
      currentPatient.subscriberLastName !== initialPatient.subscriberLastName ||
      currentPatient.daySubscriberDOB !== initialPatient.daySubscriberDOB ||
      currentPatient.secondarySubscriberFirstName !== initialPatient.secondarySubscriberFirstName ||
      currentPatient.secondarySubscriberLastName !== initialPatient.secondarySubscriberLastName ||
      currentPatient.daySecondaryDOB !== initialPatient.daySecondaryDOB ||
      currentPatient.secondaryRelationshipToPatientNew !== initialPatient.secondaryRelationshipToPatientNew ||
      currentPatient.policyHolder !== initialPatient.policyHolder;

    // If nothing important changed this session, don't run the validation
    // checks or send anything to the queue.
    if (!imagesChanged && !patientChanged) return;

    // Gate which submissions are eligible to be queued to Curve.
    const insuranceType = data.isPatinetCoveredByInsurance || '';

    // No insurance selection — nothing to send.
    if (!insuranceType) return;

    // Self-pay, MVA, Workers Comp and VA patients do not get a Curve record
    if (CURVE_EXCLUDED_INSURANCES.includes(insuranceType)) return;

    // A drivers license must be uploaded before we send anything.
    if (!current.driversLicense) return;

    // The primary insurance photos are required
    if (!current.primaryFront || !current.primaryBack) return;

    this.http.post(`${environment.BACKEND_URL}/queue/queue-data`, {
      submissionId,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      dateOfBirth: data.day || '',
      patientEmail: data.patientEmail || data.dynamicEmail || '',
      clinicId: data.selectChiroCenter || '',
      salesforceAccountId: data.salesforceAccountId || '',
      insuranceName: data.isPatinetCoveredByInsurance || '',
      address: data.addresspatientInfo || '',
      city: data.cityPatientInfo || '',
      state: data.statepatientInfo || '',
      zip: data.zipPatientInfo || '',
      relationshipToPatient: data.container6?.ifInsuredWhoIsTheMainSubscriberOrPolicyHolder === 'Self'
        ? data.container6.ifInsuredWhoIsTheMainSubscriberOrPolicyHolder
        : (data.container6?.relationshipToPatientNew || ''),
      subscriberFirstName: data?.container6?.subscriberFirstName || '',
      subscriberLastName: data?.container6?.subscriberLastName || '',
      subscriberDOB: data?.container6?.daySubscriberDOB || '',
      secondarySubscriberFirstName: data?.container4?.secondarySubscriberFirstName || '',
      secondarySubscriberLastName: data?.container4?.secondarySubscriberLastName || '',
      secondaryDOB: data?.container4?.daySecondaryDOB || '',
      secondaryRelationshipToPatient: data?.container4?.secondaryRelationshipToPatientNew || '',
      insuranceCardFrontUrl: current.primaryFront,
      insuranceCardBackUrl: current.primaryBack,
      secondaryInsuranceCardFrontUrl: current.secondaryFront,
      secondaryInsuranceCardBackUrl: current.secondaryBack,
      driversLicense: current.driversLicense,
      autoInsuranceUrl: current.autoInsurance
    }).subscribe({
      error: (err) => console.error('Failed to send insurance images to queue:', err),
    });
  }

  /**
   * When the patient is not the main policy holder, subscriber details and the
   * relationship to the patient are required. Sets a toast error and returns
   * false when any are missing; returns true otherwise.
   */
  // private validateSubscriberFields(data: any): boolean {
  //   if (data?.container6?.ifInsuredWhoIsTheMainSubscriberOrPolicyHolder !== 'Other') {
  //     return true;
  //   }

  //   const container6 = data?.container6 || {};
  //   const missingSubscriberFields = [
  //     !container6.subscriberName && !container6.subscriberFirstName ? 'Subscriber First Name' : null,
  //     !container6.subscriberName && !container6.subscriberLastName ? 'Subscriber Last Name' : null,
  //     !container6.subscriberbirthDate && !container6.daySubscriberDOB ? 'Subscriber Date of Birth' : null,
  //     !container6.relationshipToPatient && !container6.relationshipToPatientNew ? 'Relationship to Guarantor' : null,
  //   ].filter(Boolean);

  //   if (missingSubscriberFields.length > 0) {
  //     this.showError(
  //       `The following fields are required when the patient is not the main policy holder: ${missingSubscriberFields.join(', ')}.`
  //     );
  //     return false;
  //   }

  //   return true;
  // }

  // Autosave for an existing submission, edit path only
  // Baseline is taken on a delay, after FormIO applies defaults and conditionals
  private startAutosave(form: any) {
    if (!environment.AUTOSAVE_ENABLED) return;
    if (!this.submissionId) return;

    this.autosaveArmed = false;

    this.autosaveSub?.unsubscribe();
    this.autosaveSub = this.autosave.changes.subscribe((state) => {
      this.autosaveState = state;
      this.cdRef.detectChanges();
    });

    if (!this.unloadFlushHandler) {
      this.unloadFlushHandler = () => this.autosave.flushOnUnload();
      window.addEventListener('beforeunload', this.unloadFlushHandler);
      window.addEventListener('pagehide', this.unloadFlushHandler);
    }

    setTimeout(() => {
      this.autosave.start({
        submissionId: this.submissionId!,
        // Same function for baseline and reads, so a cancelled row gets removed too
        getData: () => this.dataWithOpenGridRows(),
        baseline: this.dataWithOpenGridRows(),
        canSave: () => !this.linkExpired,
        intervalMs: environment.AUTOSAVE_INTERVAL_MS,
      });
      this.autosaveArmed = true;
    }, 1500);
  }

  /**
   * Display error message
   */
  private showError(message: string) {
    this.submitError = message;
    this.cdRef.detectChanges();
    console.error(message);
  }

   private getFormPathByFormId(formId?: string): string {
    const form = formId || localStorage.getItem('formId') || '';
    console.log("form in get Form Path By FormId: ", form)

    if (form === environment.SHORTER_FORM_ID) {
        return "intakev20"; 
    }
    return "intakeform";
  }
  

  async ngOnInit() {
    this.loadingService.show();

    try {
      this.routeSub = this.route.paramMap.subscribe(async (params) => {
        this.submissionId = params.get('id');

        // Logging 'opened a new page' event into App Insights
        this.ai.logPageView('New Intake Form', window.location.href);
        
        // Logging custom event PageView into App Insights
        this.ai.logEvent('PageView', {
          page: 'New Intake Form',
          url: window.location.href,
          submissionId: this.submissionId
        });

        this.formData = null;
        this.destroyForm();
        await this.initializeComponent();
        await this.fetchJSONData();
        this.loadingService.hide();
        this.cdRef.detectChanges();
      });
    } catch (error) {
      this.loadingService.hide();
      this.cdRef.detectChanges();
    }
  }

  private destroyForm() {
    // Route changes rebuild the form, autosave must not outlive it
    this.autosave.stop();

    if (this.form) {
      this.form.destroy();
      this.form = null;
    }
  }

  private async initializeComponent() {
    try {
      if (this.submissionId) {
        this.formData = await this.getSubmissionById(this.submissionId);

        // If getSubmissionById detected an expired token (401), stop here
        if (this.linkExpired) {
          return;
        }

        if (!this.formData?.data) {
          return;
        }

        // Store the actual role value if it exists in the submission
        if (this.formData?.data?.role) {
          this.actualRoleValue = this.formData.data.role;
        }

        this.captureInitialInsuranceImages(this.formData?.data);

        const imageFields = [
				{
					data: this.formData.data.licenseUploadFront,
					key: "licenseUploadFront",
					parent: this.formData.data,
				},
				{
					data: this.formData.data.container4?.secondarylicenseUploadFront,
					key: "secondarylicenseUploadFront",
					parent: this.formData.data.container4,
				},
				{
					data: this.formData.data.container4?.secondarylicenseUploadBack,
					key: "secondarylicenseUploadBack",
					parent: this.formData.data.container4,
				},
				{
					data: this.formData.data.container6?.fileUploadFront,
					key: "fileUploadFront",
					parent: this.formData.data.container6,
				},
				{
					data: this.formData.data.container6?.fileUploadBack,
					key: "fileUploadBack",
					parent: this.formData.data.container6,
				},
				{
					data: this.formData.data.uploadYourAutoInsurance,
					key: "uploadYourAutoInsurance",
					parent: this.formData.data,
				},
			];

			this.fileUploadService.refreshTokens(imageFields);
      }
    } catch (error) {
      console.error('Error initializing component:', error);
    } finally {
      this.loadingService.hide();
      this.cdRef.detectChanges();
    }
  }

  async getSubmissionById(submissionId: string): Promise<any> {
    const cacheBuster = {
      params: {
        _: new Date().getTime(), // Prevent caching by adding a unique timestamp
      },
    };

    let sawUnauthorized = false;

    // Attempt 1: Check the NEW form database (v20)
    try {
      console.log("Attempt 1: Searching for patient in intakev20...");
      const formioNew = new Formio(`${this.projectUrl}/intakev20/submission/${submissionId}`);
      const sub = await formioNew.loadSubmission(cacheBuster);

      if (sub) {
        console.log("SUCCESS: Patient found in intakev20 database");
        this.updateStorage(sub);
        return sub;
      }
    } catch (e: any) {
      if (e?.status === 401) {
        sawUnauthorized = true;
        console.error("Access denied on intakev20 (401 Unauthorized) - token likely expired");
      } else {
        console.warn("Patient not found in v20, checking old database (intakeform)...");
      }
    }

    // Attempt 2: Check the OLD form database (intakeform)
    try {
      const formioOld = new Formio(`${this.projectUrl}/intakeform/submission/${submissionId}`);
      const sub = await formioOld.loadSubmission(cacheBuster);

      if (sub) {
        console.log("SUCCESS: Patient found in OLD database (intakeform)");
        this.updateStorage(sub);
        return sub;
      }
    } catch (e: any) {
      if (e?.status === 401) {
        sawUnauthorized = true;
        console.error("Access denied on intakeform (401 Unauthorized) - token likely expired");
      } else {
        console.error("FINAL ERROR: Patient not found in any database!");
      }
    }

    // If both attempts failed and at least one was 401, treat as expired token
    if (sawUnauthorized) {
      this.linkExpired = true;
    }
    return null;
  }

  /**
   * Helper function to refresh local storage data once the patient is found.
   * This ensures parameters are available for subsequent logic (e.g., path selection).
   */
  private updateStorage(sub: any) {
    if (sub && sub.data) {
      localStorage.setItem('clinic_code', sub.data.selectChiroCenter || '');
      localStorage.setItem('created_date', sub.created || '');
      localStorage.setItem('email_address', sub.data.patientEmail || '');
      console.log("Local storage successfully updated with patient data.");
    }
  }

  private getComponentKey(component: any): string | null {
    return component?.component?.key || component?.key || null;
  }

  private serializeFieldValue(value: any): string {
    if (value === undefined || value === null) return '';
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  // Stores the field value when the user enters the field
  private captureFieldValueOnFocus(component: any): void {
    const fieldKey = this.getComponentKey(component);
    if (!fieldKey) return;

    this.focusedComponent = component;
    this.fieldValuesOnFocus.set(fieldKey, this.serializeFieldValue(component?.getValue?.()));
  }

  // Logs FieldFilled if the value changed while the field was in focus
  private logFieldOnBlur(component: any): void {
    const fieldKey = this.getComponentKey(component);
    if (!fieldKey) return;

    if (this.getComponentKey(this.focusedComponent) === fieldKey) {
      this.focusedComponent = null;
    }

    if (!this.fieldValuesOnFocus.has(fieldKey)) return;
    const valueOnFocus = this.fieldValuesOnFocus.get(fieldKey);
    this.fieldValuesOnFocus.delete(fieldKey);

    if (fieldKey === 'chiroLabel') return;

    const fieldValue = component?.getValue?.();
    if (this.serializeFieldValue(fieldValue) === valueOnFocus) return;
    if (fieldValue === null || fieldValue === undefined || fieldValue === '') return;

    this.logFieldFilled(fieldKey, fieldValue);
  }

  // Logs the focused field when no blur is emitted, on card change, submit or leaving the page
  private flushFocusedFieldLog(): void {
    if (!this.focusedComponent) return;
    this.logFieldOnBlur(this.focusedComponent);
    this.focusedComponent = null;
  }

  private logFieldFilled(fieldKey: string, fieldValue: any): void {
    this.ai.logEvent('FieldFilled', {
      page: 'New Intake Form',
      cardName: this.currentCardName,
      field: fieldKey,
      value: fieldValue,
      submissionId: this.submissionId,
    });
  }

  ngOnDestroy() {
    this.flushFocusedFieldLog();
    // Flush what is pending, then stop, the service is a root singleton
    this.autosave.flushOnUnload();
    this.autosave.stop();
    this.autosaveSub?.unsubscribe();

    if (this.unloadFlushHandler) {
      window.removeEventListener('beforeunload', this.unloadFlushHandler);
      window.removeEventListener('pagehide', this.unloadFlushHandler);
      this.unloadFlushHandler = undefined;
    }

    if (this.currentRecordSubscription) {
      this.currentRecordSubscription.unsubscribe();
    }

    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }

    if (this.clearButtonObserver) {
      this.clearButtonObserver.disconnect();
      this.clearButtonObserver = undefined;
    }

    if (this.enterCheckboxHandler) {
      document.removeEventListener('keydown', this.enterCheckboxHandler);
      this.enterCheckboxHandler = undefined;
    }
  }

  private isPatientView(): boolean {
    return true;
  }

  async fetchJSONData() {
    if (this.linkExpired) {
      this.loadingService.hide();
      this.cdRef.detectChanges();
      return;
    }

    if (this.formData?.data?.localStatus === 'Intake Form-Submitted') {
      this.linkExpired = true;
      this.loadingService.hide();
      this.cdRef.detectChanges();
      return;
    }

    const formPath = this.getFormPathByFormId();
    try {
      let result = await Formio.request(`${this.projectUrl}/${formPath}`, 'GET');
      const formContainer = document.getElementById('formio');
      if (!formContainer) {
        console.error('Form container not found');
        return;
      }

      this.token = localStorage.getItem('formioToken') || '';

      if (this.token && this.submissionId) {
        // Edit existing submission
        Formio.createForm(formContainer, result, {
          display: 'wizard',
          noSubmit: true,
        }).then((form: any) => {
          this.form = form;
          localStorage.setItem('formioToken', this.token || '');

          form.on('error', function (err: any) {
            console.error('Form error:', err);
          });
          const submissionCopy = JSON.parse(JSON.stringify(this.formData));
          if (this.isPatientView()) {
            submissionCopy.data.role = 'Patient';
          }

          form.submission = submissionCopy;

          this.applyPanelLogic(form);
          this.changeConsentLabel(form);
          this.changePILabel(form);
          this.changeWCLabel(form);
          this.startAutosave(form);

          // Tracks the active card name used in the FieldFilled log
          form.on('change', (event: any) => {
              // Only a hint, the delta comes from the data itself
              // Before the guards below, those only filter App Insights logging
              if (this.autosaveArmed) {
                this.autosave.markDirty();
              }

              const changed = event?.changed;
              if (!changed || !changed.component || changed.value === undefined) return;
              if (changed.component.key === 'chiroLabel') return;

              const fieldKey = changed.component.key;

              if (fieldKey.toLowerCase().includes("next") || fieldKey.toLowerCase().includes("aabcdef") )
              {
                  // Logs the last field before the card changes
                  this.flushFocusedFieldLog();
                  const components = this.form.root.components[0]?.components;
                  if (!components) return;
                  const activeIndex = components.findIndex((comp: any) => !comp.collapsed);
                  const labels = document.querySelectorAll('.ml-2');
                  const cardLabel = labels[activeIndex]?.textContent?.trim() || `Card-${activeIndex}`;
                  this.currentCardName = cardLabel;
              }
            });

          // FieldFilled is logged on blur, not while typing
          form.on('focus', (component: any) => {
            this.captureFieldValueOnFocus(component);
          });

          form.on('blur', (component: any) => {
            this.logFieldOnBlur(component);
          });

          const emailComponent = form.getComponent('patientEmail');
          if (emailComponent) {
            emailComponent.disabled = true;
            emailComponent.redraw();
          }

          // Set up a change event listener that keeps track of the role value
          const roleComponent = form.getComponent('role');
          if (roleComponent) {
            roleComponent.component.hidden = true;
            roleComponent.redraw();
          }

          form.on('submit', () => {
            this.flushFocusedFieldLog();
            this.loadingService.show();
            this.submitError = null;

            // if (!this.validateSubscriberFields(form.submission.data)) {
            //   this.loadingService.hide();
            //   return;
            // }

            // Explicit save writes the whole submission and the status
            this.autosave.stop();

            // Use the actual role value for submission
            form.submission.data.role = this.actualRoleValue;
            form.submission.data.localStatus = 'Intake Form-In Process';

            Formio.request(
              `${this.projectUrl}/${formPath}/submission/${this.submissionId}`,
              'put',
              form.submission
            )
              .then(() => {
                console.log('PUT then fired, submissionId:', this.submissionId);
                this.sendImagesToQueue(this.submissionId!, form.submission.data);
                this.loadingService.hide();
                this.router.navigate(['']);
              })
              .catch((err: any) => {
                console.error('Formio PUT failed:', err);
                this.loadingService.hide();
              });
          });

          form.on('render', () => {
            setTimeout(() => {
              const cancelButton = document.querySelector(".btn-wizard-nav-cancel");
              if (cancelButton) {
                cancelButton.addEventListener("click", (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  this.router.navigate(["/patients"]);
                }, { once: true });
              }
              this.makeWizardNavSticky();
              this.skipClearButtonsOnTab();
              this.enableEnterOnCheckboxes();
            }, 100);
            this.attachCardClickListeners(form);

            const submitButton = document.querySelector(
              '.btn-wizard-nav-submit'
            );
            if (submitButton) {
              submitButton.textContent = 'Save Changes';
              this.attachGridCommitInterceptor(submitButton as HTMLButtonElement);
            }
            this.attachAddRowGuards(form);
          });
        });
      } else {
        // Create new submission
        if (this.token) {
          localStorage.setItem('formioToken', this.token);
        }

        Formio.createForm(formContainer, result, { display: 'wizard', noSubmit: true }).then(
          (form: any) => {
            this.form = form;
            localStorage.setItem('formioToken', this.token || '');

            // Initialize with Patient role for UI
            if (this.isPatientView()) {
              form.submission = {
                data: {
                  role: 'Patient', // Show Patient view
                },
              };
            }

            if (
              localStorage.getItem('email_address') &&
              localStorage.getItem('clinic_code')
            ) {
              // Merge with existing submission data if needed
              const submissionData = form.submission?.data || {};
              form.submission = {
                data: {
                  ...submissionData,
                  patientEmail: localStorage.getItem('email_address'),
                  sf_account_id: localStorage.getItem('sf_account_id'),
                  selectChiroCenter: localStorage.getItem('clinic_code'),
                  localStatus: 'Appointment-Booked',
                  role: this.isPatientView() ? 'Patient' : 'CT',
                },
              };

              const emailComponent = form.getComponent('patientEmail');
              if (emailComponent) {
                emailComponent.disabled = true;
                emailComponent.redraw();
              }

              const selectComponent = form.getComponent('selectChiroCenter');
              if (selectComponent) {
                selectComponent.setValue(localStorage.getItem('clinic_code'));
                selectComponent.disabled = true;
              }
            }

            // Hide the role field regardless
            const roleComponent = form.getComponent('role');
            if (roleComponent) {
              roleComponent.component.hidden = true;
              roleComponent.redraw();
            }

            form.on('submit', () => {
              this.loadingService.show();
              this.submitError = null;

              // if (!this.validateSubscriberFields(form.submission.data)) {
              //   this.loadingService.hide();
              //   return;
              // }

              form.submission.data.role = this.actualRoleValue;
              form.submission.data.localStatus = 'Intake Form-In Process';
              Formio.request(
                `${this.projectUrl}/${formPath}/submission`,
                'post',
                form.submission
              )
                .then((res: any) => {
                  const userJwtToken =
                    res.metadata?.['Call User Creation API']?.userJwtToken;
                  if (userJwtToken) {
                    localStorage.setItem('formioToken', userJwtToken);
                  }

                  this.sendImagesToQueue(res._id, form.submission.data);
                  this.loadingService.hide();
                  this.router.navigate(['']);
                })
                .catch((error: any) => {
                  console.error('Submission Error:', error);
                  this.loadingService.hide();
                });
            });

            form.on('render', () => {
              setTimeout(() => {
                const cancelButton = document.querySelector(".btn-wizard-nav-cancel");
                if (cancelButton) {
                  cancelButton.addEventListener("click", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.router.navigate(["/patients"]);
                  });
                }
                this.makeWizardNavSticky();
                this.skipClearButtonsOnTab();
                this.enableEnterOnCheckboxes();
              }, 100);

              const submitButton = document.querySelector(
                '.btn-wizard-nav-submit'
              );
              if (submitButton) {
                submitButton.textContent = 'Save Changes';
                this.attachGridCommitInterceptor(submitButton as HTMLButtonElement);
              }
              this.attachAddRowGuards(form);
            });
          }
        );
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      this.loadingService.hide();
    }
  }

  private makeWizardNavSticky() {
    const anchor =
      document.querySelector('.btn-wizard-nav-cancel') ||
      document.querySelector('.btn-wizard-nav-submit');
    const navContainer = anchor?.closest('ul');
    if (navContainer && !navContainer.classList.contains('sticky-wizard-nav')) {
      navContainer.classList.add('sticky-wizard-nav');
    }
  }

  private enableEnterOnCheckboxes() {
    if (this.enterCheckboxHandler) return;

    this.enterCheckboxHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const target = e.target as HTMLInputElement | null;
      if (target?.tagName === 'INPUT' && target.type === 'checkbox') {
        e.preventDefault();
        target.click();
      }
    };

    document.addEventListener('keydown', this.enterCheckboxHandler);
  }

  private skipClearButtonsOnTab() {
    // Remove non-input UI controls from the tab order so users tab straight from one field to the next
    const SKIP_SELECTOR =
      '.choices__button, [ref="tooltip"], .bi-question-circle-fill, .fa-question-circle';

    const apply = (root: ParentNode) => {
      root.querySelectorAll<HTMLElement>(SKIP_SELECTOR).forEach((el) => {
        if (el.getAttribute('tabindex') !== '-1') {
          el.setAttribute('tabindex', '-1');
        }
      });
    };

    apply(document);

    if (this.clearButtonObserver) return;

    const container = document.getElementById('formio');
    if (!container) return;

    this.clearButtonObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches?.(SKIP_SELECTOR)) {
            node.setAttribute('tabindex', '-1');
          }
          apply(node);
        });
      }
    });

    this.clearButtonObserver.observe(container, {
      childList: true,
      subtree: true,
    });
  }

  attachCardClickListeners(form: any) {
    const componentsArray = form.root.getComponents();
    if (!componentsArray?.length) return;

    const components = componentsArray[0].components;
    components.forEach((component: any, index: number) => {
      if (component.component.type !== "customPanel") return;

      const headerEl = component.refs?.header;
      if (headerEl) {
        headerEl.addEventListener("click", () => this.onCardClick(index, form));
      }
    });
  }
  onCardClick(index: number, form: any) {
    const components = form.root.components[0]?.components;
    if (!components) return;

    //this is a logic after the collapse/expansion already happens
    const clicked = components[index];
    if (clicked.collapsed) {
      return;
    }
    components.forEach((comp: any, i: number) => {
      comp.collapsed = i !== index; // collapse others, expand clicked
    });
    form.redraw();

    // Setting name for the opened card
    const labels = document.querySelectorAll('.ml-2');
    const cardLabel = labels[index]?.textContent?.trim() || `Card-${index}`
    this.currentCardName = cardLabel;
  }
  

 // The PI checkbox label is hardcoded in Form.io, so its link is rewritten here
 // The component is conditional on MVA insurance, so it is not always present
 changePILabel(form: any) {
		const checkboxComp = form.getComponent('IsPersonalInjury');
		if (!checkboxComp?.component) return;
		checkboxComp.component.label =
			`I have reviewed and accept <a target='_blank' href='${this.consentPdfUrl(PI_CONSENT_PDF)}'> Personal Injury Authorization, Assignment, Contractual Lien & Letter of Protection.</a> <span style='color:red'>*</span>`;
	}

 // Same for the workers comp download note, conditional on WC insurance
 changeWCLabel(form: any) {
		const infoComp = form.getComponent('infoForWorkersComp');
		if (!infoComp?.component) return;
		infoComp.component.information =
			`Please download these additional  <a href="${this.consentPdfUrl(WC_FORMS_PDF)}" target="_blank">Workers Compensation Forms</a>  and bring them completed along with and a copy of your accident report when you arrive for your appointment.`;
	}

 changeConsentLabel(form:any){
		const checkboxComp = form.getComponent('consentOfPrivacyInformedAA');
		// Picks branded or Alaska per file
		const pdf = (fileName: string) => this.consentPdfUrl(fileName);
		if ((this.formData.data.IsNewConsent === this.formData.data.consentOfPrivacyInformedAA) ||
			(this.formData.data.IsNewConsent === undefined && this.formData.data.consentOfPrivacyInformedAA === false)){
			checkboxComp.component.label = ` I have reviewed and accept <a target="_blank" href="${pdf('Patient Informed Consent v3-2026-01-08.pdf')}"> Patient Informed Consent Form</a>,
			<a target="_blank" href="${pdf('Payment Agreement and Authorization v3-2026-01-08.pdf')}">Payment Agreements & Authorizations</a>, `;
			if ((this.formData.data?.consentOfPrivacyInformedAA === this.formData.data?.IsScribeConsent)
				|| (this.formData.data?.consentOfPrivacyInformedAA === false && this.formData.data.IsScribeConsent === undefined)
			) {
				if (this.formData.data?.IsNewScribeConsent || !this.formData.data?.consentOfPrivacyInformedAA) {
					checkboxComp.component.label +=` <a target="_blank" href="${pdf('State Biometric Privacy Laws Disclosure and Consent New.pdf')}">  State Biometric Privacy Laws Disclosure and Consent, </a> `;
				} else {
					checkboxComp.component.label +=` <a target="_blank" href="${pdf('State Biometric Privacy Laws Disclosure and Consent.pdf')}">  State Biometric Privacy Laws Disclosure and Consent, </a> `;
				}
			}
			checkboxComp.label += ` <a target="_blank" href="${pdf('Acknowledgement of Receipt of NOPP v1-2026-01-08.pdf')}">Acknowledgement of Receipt of Notice of Privacy Practices</a>,
			and <a target="_blank" href="${pdf('Notice of Privacy Practices v2-2026-01-08.pdf')}"> Notice of Privacy Practices</a>`;
		}
		else {
			if ((this.formData.data?.consentOfPrivacyInformedAA === this.formData.data?.IsScribeConsent)
				|| (this.formData.data?.consentOfPrivacyInformedAA === false && this.formData.data.IsScribeConsent === undefined)
			) {
				if (this.formData.data?.IsNewScribeConsent || !this.formData.data?.consentOfPrivacyInformedAA) {
					checkboxComp.component.label +=` <a target="_blank" href="${pdf('State Biometric Privacy Laws Disclosure and Consent New.pdf')}">  State Biometric Privacy Laws Disclosure and Consent, </a> `;
				} else {
					checkboxComp.component.label +=` <a target="_blank" href="${pdf('State Biometric Privacy Laws Disclosure and Consent.pdf')}">  State Biometric Privacy Laws Disclosure and Consent, </a> `;
				}
			}
			if ((this.formData.data?.consentOfPrivacyInformedAA === this.formData.data?.IsTextConsentInformed)
			 || (this.formData.data?.consentOfPrivacyInformedAA === false && this.formData.data.IsTextConsentInformed === undefined)
			) {
				checkboxComp.component.label +=`, <a target="_blank" href="${pdf('Communications Consent Form v1.0.pdf')}"> Communications Consent Form</a>`;
			}
			if ((this.formData.data?.consentOfPrivacyInformedAA === this.formData.data?.IsCCOFConsent)
				|| (this.formData.data?.consentOfPrivacyInformedAA === false && this.formData.data.IsCCOFConsent === undefined)
      ) {
				checkboxComp.component.label +=`, <a target="_blank" href="${pdf('Credit Card On File (CCOF) v1.0.pdf')}"> Credit Card On File Consent Form.</a>`;
			}
		}
		 checkboxComp.component.label += ` <span style="color:red">*</span>`
	}
  applyPanelLogic(form: any) {
    const componentsArray = form.root.getComponents();
    if (!componentsArray || !componentsArray.length) return;
    const components = componentsArray[0].components;

    components.forEach((component: any, index: any) => {
      if (component.component.type !== "customPanel") return;

      // Make panel collapsible
      component.component.collapsible = true;
      component.collapsed = index !== 0;

      const panels = document.querySelectorAll('.formio-component-customPanel');
      if (panels.length > 0) {
        const firstLabel = panels[0].querySelector('.ml-2')?.textContent?.trim();
        this.currentCardName = firstLabel || "Card-0";
      }

      // Set coloring / completed state based on your data
      if (
        component.checkValidity() &&
        (this.formData.data.localStatus === "Intake Form-In Process" ||
          this.formData.data.localStatus === "Intake Form-Submitted")
      ) {
        component.component.headerCSS = "custom-panel-sucess";
        component.component.stepNumberCSS = "step-number-sucess";
        component.component.isCompleted = "true";
        component.disabled = false;
      } else if (this.formData.data.localStatus === "Appointment-Booked") {
        component.disabled = false;
      }
    });

    form.redraw();
  }

  private attachGridCommitInterceptor(submitButton: HTMLButtonElement) {
    if (!submitButton || (submitButton as any).__gridCommitAttached) return;
    (submitButton as any).__gridCommitAttached = true;
    submitButton.addEventListener(
      'click',
      async (event) => {
        // re-fired click below → let it pass through to the wizard
        if ((submitButton as any).__committed) {
          (submitButton as any).__committed = false;
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        await this.commitOpenGridRows();
        (submitButton as any).__committed = true;
        submitButton.click();
      },
      true
    );
  }

  private async commitOpenGridRows(): Promise<void> {
    const saved = this.saveAllOpenEditGridRows();
    if (saved > 0) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }

  // Data with filled but uncommitted grid rows folded in
  // FormIO keeps an open row out of submission.data until it is committed
  // Committing it here would close the row being typed in, so we copy instead
  // Empty placeholder rows are dropped, same as the explicit Save
  private dataWithOpenGridRows(): any {
    const data = this.form?.submission?.data;
    if (!data) return data;

    const form = this.form;
    if (typeof form.everyComponent !== 'function') return data;

    let augmented: any = null;

    try {
      form.everyComponent((comp: any) => {
        if (!comp || !comp.component) return;
        const type = comp.component.type;
        if (type !== 'editgrid' && type !== 'datagrid') return;
        if (comp.visible === false) return;

        const path = comp.path || comp.component.key;
        // Nested grid path has an index, not safe to write, left to the explicit Save
        if (!path || path.includes('[')) return;

        const editRows: any[] = comp.editRows || [];
        if (editRows.length === 0) return;

        const rows: any[] = [];
        let hasOpenRow = false;

        for (const row of editRows) {
          if (!row) continue;

          if (row.state === 'saved') {
            rows.push(row.data);
            continue;
          }

          if (row.state !== 'new' && row.state !== 'editing') continue;
          if (!this.isRowTouched(row)) continue;

          rows.push(row.data);
          hasOpenRow = true;
        }

        if (!hasOpenRow) return;

        augmented = augmented ?? JSON.parse(JSON.stringify(data));
        this.setValueAtPath(augmented, path, JSON.parse(JSON.stringify(rows)));
      });
    } catch (err) {
      console.warn('[autosave] could not fold open grid rows into the data:', err);
      return data;
    }

    return augmented ?? data;
  }

  private setValueAtPath(target: any, path: string, value: any): void {
    const segments = path.split('.');

    let node = target;
    for (let i = 0; i < segments.length - 1; i++) {
      const segment = segments[i];
      if (typeof node[segment] !== 'object' || node[segment] === null) {
        node[segment] = {};
      }
      node = node[segment];
    }

    node[segments[segments.length - 1]] = value;
  }

  // Wrap every EditGrid's addRow so clicking "Add Another X" cannot leave two
  // open rows in the same grid. If a previous row is open + filled, auto-save it.
  // If it's open + empty, block the new row entirely.
  private attachAddRowGuards(form: any) {
    if (!form || typeof form.everyComponent !== 'function') return;
    form.everyComponent((comp: any) => {
      if (!comp || !comp.component) return;
      if (comp.component.type !== 'editgrid') return;
      if ((comp as any).__addRowGuarded) return;
      if (typeof comp.addRow !== 'function') return;

      (comp as any).__addRowGuarded = true;
      const gridKey = comp.component?.key || '(unknown)';
      const originalAddRow = comp.addRow.bind(comp);

      comp.addRow = (...args: any[]) => {
        const editRows: any[] = comp.editRows || [];
        for (let i = 0; i < editRows.length; i++) {
          const row = editRows[i];
          if (!row) continue;
          if (row.state !== 'new' && row.state !== 'editing') continue;

          if (!this.isRowTouched(row)) {
            console.log(`[editgrid:${gridKey}] addRow blocked: empty open row at index ${i}`);
            return undefined;
          }

          // filled — auto-save before opening a new row
          try {
            comp.saveRow(i, true);
          } catch (err) {
            console.warn(`[editgrid:${gridKey}] saveRow failed:`, err);
          }

          // if still open after save attempt, block (validation failed)
          if (row.state === 'new' || row.state === 'editing') {
            console.log(`[editgrid:${gridKey}] addRow blocked: row ${i} did not save (validation?)`);
            return undefined;
          }
        }

        return originalAddRow(...args);
      };
    });
  }

  // Goes through every component in the form and commits any open rows in every EditGrid / DataGrid
  private saveAllOpenEditGridRows(): number {
    const form = this.form;
    if (!form || typeof form.everyComponent !== 'function') return 0;

    const grids: any[] = [];
    try {
      form.everyComponent((comp: any) => {
        if (!comp || !comp.component) return;
        const type = comp.component.type;
        if (type !== 'editgrid' && type !== 'datagrid') return;
        if (comp.visible === false) return;
        grids.push(comp);
      });
    } catch (err) {
      console.warn('[commitOpenGridRows] everyComponent walk failed:', err);
      return 0;
    }

    let savedCount = 0;
    for (const grid of grids) {
      const gridKey = grid.component?.key || grid.key || '(unknown grid)';
      const editRows: any[] = grid.editRows || [];

      const newDataValue: any[] = [];
      let changedAny = false;

      for (let i = 0; i < editRows.length; i++) {
        const row = editRows[i];
        if (!row) continue;
        const state = row.state;

        if (state === 'saved') {
          newDataValue.push(row.data);
          continue;
        }

        if (state !== 'new' && state !== 'editing') continue;

        if (!this.isRowTouched(row)) {
          // empty placeholder — drop it
          changedAny = true;
          continue;
        }

        // filled open row — commit it
        newDataValue.push(row.data);
        changedAny = true;
        savedCount++;
      }

      if (changedAny) {
        try {
          if (typeof grid.setValue === 'function') {
            grid.setValue(newDataValue, { modified: true });
          } else {
            grid.dataValue = newDataValue;
          }
        } catch (err) {
          console.warn(
            `[commitOpenGridRows] setValue failed for grid '${gridKey}':`,
            err
          );
          try {
            grid.dataValue = newDataValue;
          } catch {
            /* ignore */
          }
        }
      }
    }

    if (savedCount > 0) {
      try {
        form.triggerChange?.({ modified: true });
      } catch {
        // best-effort
      }
    }

    console.log(`[commitOpenGridRows] committed ${savedCount} row(s)`);
    return savedCount;
  }

  // True when the user actually interacted with this row's fields
  private isRowTouched(row: any): boolean {
    if (!row) return false;
    if (this.anyDescendantTouched(row)) return true;
    return !this.isDataShapeEmpty(row.data);
  }

  private anyDescendantTouched(obj: any): boolean {
    if (!obj || typeof obj !== 'object') return false;
    if (obj.pristine === false) return true;
    if (obj.dirty === true) return true;
    if (obj.isModified === true) return true;

    const kids: any[] = Array.isArray(obj.components)
      ? obj.components
      : obj.components && typeof obj.components === 'object'
        ? Object.values(obj.components)
        : [];

    for (const k of kids) {
      if (this.anyDescendantTouched(k)) return true;
    }
    return false;
  }

  // true if the data object holds no meaningful (non-default) values
  private isDataShapeEmpty(rowData: any): boolean {
    if (rowData === null || rowData === undefined) return true;
    if (typeof rowData !== 'object') {
      return rowData === '' || rowData === false;
    }
    for (const key in rowData) {
      const v = rowData[key];
      if (v === null || v === undefined || v === '' || v === false) continue;
      if (Array.isArray(v) && v.length === 0) continue;
      if (typeof v === 'object') {
        if (!this.isDataShapeEmpty(v)) return false;
        continue;
      }
      return false;
    }
    return true;
  }

}
// QA branch
